import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Modal from './Modal';
import translate from '../translate';
import Notifications from '../../services/notifications';
import ConfigsContext from '../../contextProviders/ConfigsContext';
import CloseIcon from '../icons/icon/CloseIcon';
import PdfViewerComponent from '../fileViewers/PdfViewerComponent';
import MarketProductForm from '../propTypes/marketProductForm';
import { compose, handleUploadFile } from '../../services/utilityFunctions';
import withCSRF from '../withCSRF';
import OrderProp from '../../sections/shop/propTypes/orderProp';
import { OPPORTUNITIES_STATUS } from '../../sections/opportunities/components/helpers/opportunitiesListHelper';
import { fillFormFields } from '../../sections/journey/helper/journeyHelper';
import UserProp from '../propTypes/userProp';

const pdfToolbar = [
  {
    type: 'sidebar-thumbnails',
  },
  {
    type: 'sidebar-annotations',
  },
  {
    type: 'pager',
  },
  {
    type: 'zoom-out',
  },
  {
    type: 'zoom-in',
  },
  {
    type: 'zoom-mode',
  },
  {
    type: 'annotate',
  },
  {
    type: 'signature',
  },
  {
    type: 'search',
  },
  {
    type: 'debug',
  },
];

function PdfFormModal(props) {
  const {
    t,
    _csrf,
    form,
    onClose,
    id,
    wrapperClass,
    showSave,
    isDisabled,
    opportunity,
    userToFillForm,
    isEditable,
  } = props;
  const {
    productId,
    productForm,
    providerData: formProviderData,
    formAnswersLink,
  } = form;
  const providerData = userToFillForm
    ? fillFormFields(formProviderData, userToFillForm)
    : formProviderData;
  const { templatePDF } = productForm;
  const isStillPending =
    isEditable ||
    !opportunity?.status ||
    opportunity?.status === OPPORTUNITIES_STATUS.PENDING;

  const document = templatePDF?.[0];
  const { tenant } = useContext(ConfigsContext);
  const { pspdfkitLicenseKey } = tenant?.configs;
  const [isLoading, setIsLoading] = useState(false);

  const pdfViewerRef = useRef(null);

  const handleSave = async () => {
    if (pdfViewerRef.current) {
      try {
        setIsLoading(true);
        const formFields = await pdfViewerRef.current.exportFormFields();

        const errors = [];
        const errorPromises = formFields?.formFields?.map(async field => {
          const { name, type, flags } = field;
          const fieldValue = formFields?.formFieldValues?.find(
            v => v.name === name,
          );
          const isRequired = flags?.includes('required');
          const isSignature = type === 'pspdfkit/form-field/signature';

          if (isRequired) {
            if (!isSignature) {
              const hasNoValue =
                !fieldValue?.value || fieldValue.value.length === 0;
              if (hasNoValue) {
                return name;
              }
            } else {
              const signature =
                await pdfViewerRef.current.checkForSignatureField(name);
              if (!signature) {
                return name;
              }
            }
          }
          return null;
        });

        const results = await Promise.all(errorPromises);
        errors.push(...results.filter(name => name));
        if (errors.length > 0) {
          Notifications.showNotificationError(
            t('error'),
            t('fill.and.sign.error.required', errors.length, errors.join(', ')),
          );
          setIsLoading(false);
          return;
        }

        const data = await pdfViewerRef.current.exportPDF();

        const name = formAnswersLink
          ? formAnswersLink.split('/')[formAnswersLink.split('/').length - 1]
          : null;
        const pdfLink = await handleUploadFile(
          _csrf,
          null,
          {
            ...data,
            originalName: name,
          },
          name,
        );

        onClose(productId, formFields, pdfLink?.url);
        setIsLoading(false);
      } catch (err) {
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
        setIsLoading(false);
      }
    }
  };

  const saveClasses = cx(
    'btn',
    'btn-dynamic',
    'save-survey-form',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  return (
    <Modal onClose={onClose} id={id}>
      <div
        className={`modal-dialog modal-dialog-centered modal-lg ${wrapperClass}`}
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('form.preview')}</h5>
            <button type="button" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="form-pdf-container">
            <div className="container">
              <PdfViewerComponent
                t={t}
                ref={pdfViewerRef}
                documentFile={
                  isStillPending ? document : { url: formAnswersLink }
                }
                id="viewer"
                pspdfkitLicenseKey={pspdfkitLicenseKey}
                isFormCreator={!isDisabled || isStillPending}
                pdfToolbar={pdfToolbar}
                originalName={document?.originalName}
                formFields={
                  isStillPending && Object.values(providerData)?.length > 0
                    ? providerData
                    : null
                }
                disabled={isDisabled}
                height="75vh"
              />
            </div>
          </div>
          {!isDisabled && showSave ? (
            <div className="save-container">
              <button
                className={saveClasses}
                type="button"
                onClick={handleSave}
                disabled={isLoading}
              >
                {t('save_btn')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

PdfFormModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string,
  wrapperClass: PropTypes.string,
  form: PropTypes.shape({
    formAnswerData: PropTypes.shape({}),
    productId: PropTypes.string,
    productForm: PropTypes.shape(MarketProductForm.propType),
    providerData: PropTypes.shape({}),
    formAnswersLink: PropTypes.string,
  }).isRequired,
  showSave: PropTypes.bool,
  isDisabled: PropTypes.bool,
  opportunity: PropTypes.shape(OrderProp.propType).isRequired,
  userToFillForm: PropTypes.shape(UserProp.propType),
  isEditable: PropTypes.bool,
};

PdfFormModal.defaultProps = {
  id: 'formModal',
  wrapperClass: '',
  showSave: true,
  isDisabled: false,
  userToFillForm: null,
  isEditable: false,
};

export default compose(translate, withCSRF)(PdfFormModal);
